import { Outlet, useNavigate, NavLink, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { LogOut, LayoutDashboard, Send, Loader2 } from 'lucide-react';
import { usePartnerAuth } from '../../context/PartnerAuthContext';
import { toast } from 'sonner';

export default function PartnerLayout() {
  const nav = useNavigate();
  const { partner, loading, logout } = usePartnerAuth();

  useEffect(() => {
    if (!loading && !partner) nav('/partner/login', { replace: true });
  }, [partner, loading, nav]);

  if (loading || !partner) {
    return (
      <div className="min-h-screen bg-[#F5F1E8] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#0B3B2E]" />
      </div>
    );
  }

  const initials = partner.name.split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();

  const handleLogout = () => {
    logout();
    toast.success('Signed out');
    nav('/partner/login', { replace: true });
  };

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-2.5 rounded-lg text-[14px] transition-all ${
      isActive
        ? 'bg-[#0B3B2E] text-[#F5F1E8] font-medium'
        : 'text-[#0E1F1A]/75 hover:bg-[#0B3B2E]/8 hover:text-[#0B3B2E]'
    }`;

  return (
    <div className="min-h-screen bg-[#F5F1E8] flex">
      <aside className="hidden lg:flex w-[260px] flex-col border-r border-[#0B3B2E]/10 bg-white/40">
        <Link to="/partner" className="h-[72px] px-6 flex items-center gap-2 border-b border-[#0B3B2E]/10">
          <div className="w-9 h-9 rounded-full bg-[#0B3B2E] flex items-center justify-center">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M4 20 L12 4 L20 20" stroke="#C8944B" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div>
            <p className="font-serif text-[18px] font-semibold text-[#0B3B2E] leading-none">
              Vector<span className="text-[#C8944B]">FX</span>
            </p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#0E1F1A]/50 mt-1">Partner</p>
          </div>
        </Link>

        <nav className="flex-1 p-4 space-y-1">
          <NavLink to="/partner" end className={linkClass}>
            <LayoutDashboard className="w-4 h-4" /> Dashboard
          </NavLink>
          <NavLink to="/partner/leads/new" className={linkClass}>
            <Send className="w-4 h-4" /> Submit a referral
          </NavLink>
        </nav>

        <div className="p-4 border-t border-[#0B3B2E]/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#0B3B2E] text-[#C8944B] font-medium flex items-center justify-center">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-medium text-[#0B3B2E] truncate">{partner.name}</p>
              <p className="text-[11px] text-[#0E1F1A]/55 truncate">{partner.company}</p>
            </div>
            <button onClick={handleLogout} className="text-[#0E1F1A]/55 hover:text-[#0B3B2E]" title="Sign out">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      <div className="lg:hidden fixed top-0 inset-x-0 z-40 h-14 bg-white border-b border-[#0B3B2E]/10 flex items-center justify-between px-4">
        <Link to="/partner" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#0B3B2E] flex items-center justify-center">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M4 20 L12 4 L20 20" stroke="#C8944B" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <span className="font-serif text-[15px] font-semibold text-[#0B3B2E]">Partner</span>
        </Link>
        <button onClick={handleLogout} className="text-[13px] text-[#0B3B2E]">Sign out</button>
      </div>

      <div className="lg:hidden fixed top-14 inset-x-0 z-30 bg-white border-b border-[#0B3B2E]/10 flex overflow-x-auto">
        <NavLink to="/partner" end className={({ isActive }) => `px-4 py-2.5 text-[13px] whitespace-nowrap ${isActive ? 'text-[#0B3B2E] font-medium border-b-2 border-[#0B3B2E]' : 'text-[#0E1F1A]/60'}`}>Dashboard</NavLink>
        <NavLink to="/partner/leads/new" className={({ isActive }) => `px-4 py-2.5 text-[13px] whitespace-nowrap ${isActive ? 'text-[#0B3B2E] font-medium border-b-2 border-[#0B3B2E]' : 'text-[#0E1F1A]/60'}`}>New referral</NavLink>
      </div>

      <main className="flex-1 min-w-0 pt-24 lg:pt-0">
        <Outlet />
      </main>
    </div>
  );
}
